
import { getSpecFields } from '@/utils/specFieldMap'
import { getCategoriesForSelector, getCategoryName, fetchCategoryDictionary } from '@/utils/category-dictionary'
import { addProductImage, removeProductImage, reorderProductImages } from '@/services/productImageApi'
import { resolveProductImageUrls, computeMaxCropRect } from '@/utils/image'

export default {
  name: 'AdminProductEdit',
  data () {
    return {
      isEdit: false,
      productId: null,
      loading: false,
      saving: false,
      showCategoryPicker: false,
      showDeleteConfirm: false,
      showCropper: false,
      cropperImage: '',
      // 图片管理（2026-09-19 收口）：上限与后端 image-admin MAX_IMAGES 对齐；
      // 预览态 + 长按拖拽排序态（active 时幽灵图跟手，落点格高亮）
      MAX_IMAGES: 9,
      previewUrl: '',
      previewIndex: 0,
      dragJustEnded: false,   // 拖拽结束后的短暂窗口：吞掉合成 click 防误触预览
      dragState: {
        active: false,   // 长按已激活拖拽
        from: -1,        // 起始格
        target: -1,      // 当前悬停目标格
        x: 0, y: 0,      // 幽灵图坐标（视口 px）
        startX: 0, startY: 0,
        timer: null,
        rects: []        // 激活时缓存的各格位置（落点判定）
      },
      // 裁剪比例锁：1:1 默认（商品主图规范，与 Lite image-admin 一致）；null = 自由裁剪
      cropRatio: '1:1',
      cropSizing: false,   // 抓到缩放点时才显示裁切 handles（平时弱化，pan-first）
      ratioPresets: [
        { key: '1:1', label: '1:1', number: [1, 1] },
        { key: '4:3', label: '4:3', number: [4, 3] },
        { key: '16:9', label: '16:9', number: [16, 9] },
        { key: '9:16', label: '9:16', number: [9, 16] },
        { key: 'free', label: '自由', number: null }
      ],
      form: {
        name: '',
        description: '',
        price: null,
        original_price: null,
        stock: null,
        brand: '',
        model: '',
        specification: '',
        category: '',
        tier: '正',
        units: [{ unit_name: '个', ratio: 1, is_base: 1 }],
        status: 'active',
        is_recommended: false,
        inner_code: '',
        repair_difficulty: 3,
        admin_override_rating: 0,
        admin_override_enabled: false,
        weight: 100,
        images: '[]',
        marketing_image: '',
        // 规格参数(JSON对象) + 适用车型(JSON数组) + 搜索标签(JSON数组)，与 PC 端一致
        product_specs: {},
        applicable_models: [],
        search_tags: [],
        search_code: ''
      },
      images: [],
      frameOptions: [], // active 商品边框选项（边框系统一期，编辑弹窗 radio 缩略预览）
      searchTerms: [],
      newSearchTerm: { term: '', term_type: 'alias', weight: 5 },
      // 分类选择器选项（初始=内置字典；created 拉取 /categories 后换 API active 品类）
      categories: getCategoriesForSelector(),
      // 规格字典 + 车型字典（从 product_tags 加载，fetchTags 填充）
      specOptions: {},
      availableModels: [],
      modelKeyword: '',
      searchTagsInput: ''
    }
  },
  computed: {
    activeRatio () {
      return this.ratioPresets.find(r => r.key === this.cropRatio) || this.ratioPresets[0]
    },
    categoryLabel () {
      const cat = this.categories.find(c => c.value === this.form.category)
      return cat ? cat.label : '请选择分类'
    },
    // 基础单位名（单位配置区换算提示用，如“1箱=24个”中的“个”）
    baseUnitName () {
      const b = (this.form.units || []).find(u => u.is_base)
      return b ? (b.unit_name || '').trim() : ''
    },
    // 当前分类的规格字段（动态，来自 @/utils/specFieldMap）
    currentSpecFields () {
      return getSpecFields(this.form.category)
    },
    // 适用车型搜索过滤：匹配关键词；已选车型始终保留（与 PC 一致）
    filteredModels () {
      const kw = (this.modelKeyword || '').trim().toLowerCase()
      if (!kw) return this.availableModels
      return this.availableModels.filter(m => {
        const matched = (m.tag_label || '').toLowerCase().includes(kw) || (m.tag_value || '').toLowerCase().includes(kw)
        return matched || this.isModelSelected(m.tag_value)
      })
    }
  },
  watch: {
    // 监听路由变化，处理在同一页面内切换编辑不同商品的情况
    '$route' (to, from) {
      const newProductId = to.params.id
      if (newProductId !== this.productId) {
        this.productId = newProductId
        this.isEdit = !!newProductId
        this.resetForm()
        if (this.isEdit) {
          this.fetchProduct()
        }
      }
    }
  },
  created () {
    this.productId = this.$route.params.id
    this.isEdit = !!this.productId
    this.resetForm()
    this.fetchTags()   // 加载规格/车型字典（新增与编辑都需要，与 PC editProduct 同源）
    // 品类字典 API 主源（active 品类+经营者排序）：到位后重建选择器选项
    fetchCategoryDictionary().then(() => this.syncCategoryOptions())
    if (this.isEdit) {
      this.fetchProduct()
    }
  },
  methods: {
    // 分类选择器选项对齐（品类数据化第一阶段）：API active 品类为主源；编辑商品当前品类
    // 不在 active（停用/未知）时追加「xxx（已停用）」保值项——不清空、保存不改写原 slug
    syncCategoryOptions () {
      const opts = getCategoriesForSelector().map(o => ({ ...o }))
      const cur = this.form.category
      if (cur && !opts.some(o => o.value === cur)) {
        opts.push({ value: cur, label: getCategoryName(cur) + '（已停用）' })
      }
      this.categories = opts
    },
    resetForm () {
      // 重置表单为初始状态
      this.form = {
        name: '',
        description: '',
        price: null,
        original_price: null,
        stock: null,
        brand: '',
        model: '',
        specification: '',
        category: '',
        tier: '正',
        units: [{ unit_name: '个', ratio: 1, is_base: 1 }],
        status: 'active',
        is_recommended: false,
        inner_code: '',
        repair_difficulty: 3,
        admin_override_rating: 0,
        admin_override_enabled: false,
        weight: 100,
        images: '[]',
        marketing_image: '',
        product_specs: {},
        applicable_models: [],
        search_tags: [],
        search_code: ''
      }
      this.images = []
      this.searchTagsInput = ''
      this.modelKeyword = ''
      this.showDeleteConfirm = false
    },
    async fetchProduct () {
      if (!this.productId) {
        console.error('[AdminProductEdit] fetchProduct: productId is empty')
        return
      }

      console.log('[AdminProductEdit] 开始获取商品数据, productId:', this.productId)
      this.loading = true
      try {
        const res = await this.axios.get(`/admin/products/${this.productId}`)
        console.log('[AdminProductEdit] API响应:', res.data)

        if (res.data.code !== 200) {
          throw new Error(res.data.message || '获取商品失败')
        }
        const data = res.data.data
        console.log('[AdminProductEdit] 商品数据:', data)

        // 精确填充表单字段，避免遗漏
        this.form = {
          name: data.name || '',
          description: data.description || '',
          price: data.price || null,
          original_price: data.original_price || null,
          stock: data.stock !== undefined ? data.stock : null,
          brand: data.brand || '',
          model: data.model || '',
          specification: data.specification || '',
          category: data.category || '',
          tier: data.tier || '正',
          status: data.status || 'active',
          is_recommended: Boolean(data.is_recommended),
          inner_code: data.inner_code || '',
          repair_difficulty: data.repair_difficulty || 3,
          admin_override_rating: data.admin_override_rating || 0,
          admin_override_enabled: !!data.admin_override_enabled,
          weight: data.weight || 100,
          images: data.images || '[]',
          marketing_image: data.marketing_image || '',
          search_code: data.search_code || '',
          frame_id: '',
          frame_mode: 'inherit'
        }
        // 边框三态（边框系统二期）：active 选项 + 本商品当前态（mode + 绑定），独立容错不阻塞编辑
        this.axios.get('/admin/product-frames/options').then(r => {
          if (r.data.code === 200) this.frameOptions = r.data.data || []
        }).catch(() => {})
        this.axios.get(`/admin/products/${this.productId}/frame`).then(r => {
          if (r.data.code === 200 && r.data.data && r.data.data.mode) {
            this.form.frame_mode = r.data.data.mode
            this.form.frame_id = r.data.data.frame ? r.data.data.frame.id : ''
          }
        }).catch(() => {})
        // 选择器选项对齐 API active 品类；本商品若属停用/未知品类，追加旧值项（保存仍写原 slug）
        this.syncCategoryOptions()

        // 单位配置：后端 units 优先，空则从旧字段（unit/pack_unit）构造（与 PC 端一致）
        const pu = data.units
        if (Array.isArray(pu) && pu.length) {
          this.form.units = pu.map(u => ({
            unit_name: (u.unit_name || u.name || '').trim(),
            ratio: u.is_base ? 1 : (Number(u.ratio != null ? u.ratio : u.ratio_to_base) || 1),
            is_base: u.is_base ? 1 : 0
          }))
        } else {
          const list = [{ unit_name: data.unit || '个', ratio: 1, is_base: 1 }]
          if (data.pack_unit && Number(data.pack_quantity) > 1) list.push({ unit_name: data.pack_unit, ratio: Number(data.pack_quantity), is_base: 0 })
          this.form.units = list
        }

        // 规格参数 / 适用车型 / 搜索标签（后端返回 JSON 字符串，统一 parse；与 PC 端一致）
        let productSpecs = data.product_specs
        if (productSpecs && typeof productSpecs === 'string') {
          try { productSpecs = JSON.parse(productSpecs) } catch (e) { productSpecs = {} }
        }
        this.form.product_specs = productSpecs || {}

        let applicableModels = data.applicable_models
        if (applicableModels && typeof applicableModels === 'string') {
          try { applicableModels = JSON.parse(applicableModels) } catch (e) { applicableModels = [] }
        }
        this.form.applicable_models = applicableModels || []

        let searchTags = data.search_tags
        if (searchTags && typeof searchTags === 'string') {
          try { searchTags = JSON.parse(searchTags) } catch (e) { searchTags = [] }
        }
        this.form.search_tags = searchTags || []
        this.searchTagsInput = (searchTags || []).join(', ')

        // 搜索词（详情接口已附带）
        this.searchTerms = data.search_terms || []

        // 图片列表初始化 = 服务端现状归一（2026-09-19 P0 修复）：统一走
        // resolveProductImageUrls（images 数组优先，空则 fallback [image]——存量单图形态），
        // 不再只读 data.images 把 52 件 image-only 商品误判为无图；也不依赖本地上传累积
        this.images = resolveProductImageUrls(data)
      } catch (err) {
        console.error('[AdminProductEdit] 获取商品失败:', err)
        this.$toast?.fail?.('获取商品信息失败')
      } finally {
        this.loading = false
      }
    },
    toggleStatus () {
      this.form.status = this.form.status === 'active' ? 'inactive' : 'active'
    },
    // 单位层级元信息（2026-09-19 单位层级收口，与 PC 端一致）：层级=按换算比（相对基础单位
    // 绝对值）升序的秩，与保存后后端 normalizeInput 排序一致——绝不按行序/单位名推导。
    // 返回 null=数据不全（刚加的空行）；双换算相对上一级仅整除时给出（1件=10盒·200个）。
    unitLevelMeta (u) {
      if (!u || u.is_base || !u.unit_name || !(Number(u.ratio) > 1)) return null
      const valid = (this.form.units || []).filter(x => x.unit_name && (x.is_base || Number(x.ratio) > 1))
      const sorted = valid.slice().sort((a, b) => (a.is_base === b.is_base ? Number(a.ratio) - Number(b.ratio) : (a.is_base ? -1 : 1)))
      const idx = sorted.indexOf(u)
      if (idx <= 0) return null
      const tag = idx === 1 ? '中包装' : (idx === 2 ? '外包装' : `第${idx + 1}级`)
      const prev = sorted[idx - 1]
      const r = Number(u.ratio)
      const pr = Number(prev.ratio) || 1
      const rel = (pr > 1 && r > pr && r % pr === 0) ? (r / pr) : null
      const dual = `1${u.unit_name}=${rel ? `${rel}${prev.unit_name}·` : ''}${r}${this.baseUnitName}`
      return { tag, dual }
    },
    // 添加一个包装单位行（基础单位唯一且不可删）
    addUnit () {
      this.form.units.push({ unit_name: '', ratio: '', is_base: 0 })
    },
    removeUnit (idx) {
      if (this.form.units[idx] && this.form.units[idx].is_base) return
      this.form.units.splice(idx, 1)
    },
    // 加载规格/车型字典（/admin/product-tags，与 PC fetchTags 同源同接口）
    async fetchTags () {
      try {
        const res = await this.axios.get('/admin/product-tags')
        if (res.data.code === 200) {
          const tags = res.data.data || []
          this.availableModels = tags.filter(t => t.tag_type === 'model')
          this.specOptions = {}
          tags.filter(t => t.tag_type && t.tag_type.startsWith('spec_')).forEach(t => {
            if (!this.specOptions[t.tag_type]) this.specOptions[t.tag_type] = []
            this.specOptions[t.tag_type].push(t)
          })
        }
      } catch (err) {
        console.error('获取标签字典失败:', err)
      }
    },
    isModelSelected (modelValue) {
      return this.form.applicable_models && this.form.applicable_models.includes(modelValue)
    },
    toggleModel (modelValue) {
      if (!this.form.applicable_models) this.form.applicable_models = []
      const idx = this.form.applicable_models.indexOf(modelValue)
      if (idx > -1) this.form.applicable_models.splice(idx, 1)
      else this.form.applicable_models.push(modelValue)
    },
    // 规格变更时联动老字段 specification（与 PC 一致：选中值拼接成文本）
    updateSpecLabel () {
      const specs = this.form.product_specs
      const parts = Object.values(specs).filter(v => v !== '' && v !== null && v !== undefined)
      if (parts.length > 0) this.form.specification = parts.join(' ')
    },
    updateSearchTags () {
      this.form.search_tags = this.searchTagsInput.split(/[,，]/).map(s => s.trim()).filter(Boolean)
    },
    selectCategory (value) {
      this.form.category = value
      this.showCategoryPicker = false
    },
    takePhoto () {
      this.$refs.cameraInput.click()
    },
    chooseFromAlbum () {
      this.$refs.albumInput.click()
    },
    onFileSelected (e) {
      const file = e.target.files[0]
      // 清空input，允许重复选择/拍摄同一文件（浏览器认为值没变就不触发 change）
      e.target.value = ''
      if (!file) return

      // 检查文件类型
      if (!file.type.includes('image/')) {
        this.$toast?.fail?.('请选择图片文件')
        return
      }

      // 原文件上限 20MB（对齐 Lite image-admin）：手机拍照原图普遍 5-15MB，
      // 真正上传的是裁剪后输出，体积控制在 uploadCroppedImage 里做
      if (file.size > 20 * 1024 * 1024) {
        this.$toast?.fail?.('图片不能超过20MB，请换一张')
        return
      }

      // 读取图片用于裁剪
      const reader = new FileReader()
      reader.onload = (event) => {
        this.cropperImage = event.target.result
        // 每次打开重置比例锁到 1:1（商品主图规范，与 Lite image-admin 同行为）
        this.cropRatio = '1:1'
        // 使用nextTick确保DOM更新后再显示裁剪器
        this.$nextTick(() => {
          this.showCropper = true
        })
      }
      reader.onerror = () => {
        console.error('图片读取失败')
        this.$toast?.fail?.('图片读取失败')
      }
      reader.readAsDataURL(file)
    },
    cancelCrop () {
      this.showCropper = false
      this.cropperImage = ''
    },
    setCropRatio (r) {
      if (this.cropRatio === r.key) return
      this.cropRatio = r.key
      // vue-cropper 0.5.8 不 watch fixed/fixedNumber，须手动重排裁剪框套用新比例；
      // 统一整改：切比例后同样按新比例最大化（不再是 80% 容器小框）
      this.$nextTick(() => this.maximizeCrop())
    },
    // 图片就绪 → 初始框按目标比例最大化。容器测量未落定（弹窗刚开）时延迟重试
    onCropperImgLoad (result) {
      if (result !== 'success') return
      this.maximizeCrop(0)
    },
    // 按当前比例档（activeRatio.number；free=图片自身比例整图）最大化裁切框。
    // rotate-aware：90° 奇数倍旋转时真实宽高交换（与 vue-cropper goAutoCrop 同口径）
    maximizeCrop (attempt) {
      const c = this.$refs.cropper
      if (!c) return
      if (!c.w || !c.h || !c.trueWidth) {
        if (attempt < 3) setTimeout(() => this.maximizeCrop(attempt + 1), 250)
        return
      }
      const swap = Math.abs(c.rotate) % 2 > 0
      const imgW = (swap ? c.trueHeight : c.trueWidth) * c.scale
      const imgH = (swap ? c.trueWidth : c.trueHeight) * c.scale
      const n = this.activeRatio.number
      const aspect = n ? n[0] / n[1] : imgW / imgH
      const rect = computeMaxCropRect(imgW, imgH, aspect)
      if (rect) c.goAutoCrop(rect.width, rect.height)
    },
    // 只在抓到缩放点/边线时亮出 handles（移动构图不触发），抬起归位
    onCropPointerDown (e) {
      const t = e.target
      if (t && t.closest && t.closest('.crop-point, .crop-line')) {
        this.cropSizing = true
        const up = () => {
          this.cropSizing = false
          window.removeEventListener('mouseup', up)
          window.removeEventListener('touchend', up)
        }
        window.addEventListener('mouseup', up)
        window.addEventListener('touchend', up)
      }
    },
    confirmCrop () {
      if (!this.$refs.cropper) {
        this.$toast?.fail?.('裁剪器未就绪')
        return
      }

      try {
        // 使用 getCropBlob 获取裁剪后的图片 blob
        this.$refs.cropper.getCropBlob((blob) => {
          if (blob) {
            console.log('裁剪成功，blob size:', blob.size)
            this.uploadCroppedImage(blob)
          } else {
            this.$toast?.fail?.('裁剪失败，请重试')
          }
        })
      } catch (err) {
        console.error('裁剪失败:', err)
        this.$toast?.fail?.('裁剪失败，请重试')
      }
    },
    async uploadCroppedImage (blob) {
      try {
        // 后端 5MB 硬限：裁剪输出超 4.5MB 时缩到最长边 1600 重编码（对齐 Lite image-admin 约束）
        const finalBlob = await this.shrinkBlob(blob)
        const formData = new FormData()
        formData.append('purpose', 'product')
        // 裁剪输出固定 jpeg；文件名自生成，不依赖相机/相册给的原始名（无扩展名/HEIC 名会被后端拒绝）
        formData.append('file', finalBlob, `photo_${Date.now()}.jpg`)
        this.doUpload(formData)
      } catch (err) {
        console.error('图片处理失败:', err)
        this.$toast?.fail?.('图片处理失败')
      }
    },
    // 超 4.5MB 的 jpeg（vue-cropper 输出无尺寸上限，手机原图全选裁剪 quality=1 易超）重采样后返回
    shrinkBlob (blob) {
      return new Promise((resolve) => {
        if (blob.size <= 4.5 * 1024 * 1024) return resolve(blob)
        const url = URL.createObjectURL(blob)
        const img = new Image()
        img.onload = () => {
          URL.revokeObjectURL(url)
          const scale = Math.min(1, 1600 / Math.max(img.width, img.height))
          const canvas = document.createElement('canvas')
          canvas.width = Math.round(img.width * scale)
          canvas.height = Math.round(img.height * scale)
          canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height)
          canvas.toBlob(b => resolve(b || blob), 'image/jpeg', 0.9)
        }
        img.onerror = () => { URL.revokeObjectURL(url); resolve(blob) }
        img.src = url
      })
    },
    async doUpload (formData) {
      try {
        const res = await this.axios.post('/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        // 后端返回格式: { code: 200, data: { url, key, storage } }
        if (res.data.code === 200 && res.data.data?.url) {
          const url = res.data.data.url
          // 编辑态：上传成功即绑定（专用 API，追加末尾不抢主图）——不等问题点「保存」，
          // 避免「传完图没点保存就返回」丢图；新建态无商品 id，随表单一起提交
          if (this.isEdit && this.productId) {
            try {
              await addProductImage(this.productId, url)
            } catch (bindErr) {
              console.error('图片绑定失败:', bindErr)
              this.$toast?.fail?.('已上传但绑定失败：' + (bindErr?.response?.data?.message || '请重试'))
              return   // 保留裁剪页，可重试
            }
          }
          this.images.push(url)
          this.$toast?.success?.('上传成功')
          // 关闭裁剪弹窗
          this.showCropper = false
          this.cropperImage = ''
        } else {
          throw new Error(res.data.message || '上传失败')
        }
      } catch (err) {
        console.error('上传失败:', err)
        this.$toast?.fail?.('图片上传失败')
      }
    },
    // 删除图片：编辑态即时走专用 API（移除引用，删主图时服务端顺延第二张为主图）；新建态本地移除
    async removeImage (index) {
      const url = this.images[index]
      if (!url) return
      if (this.isEdit && this.productId) {
        try {
          await removeProductImage(this.productId, url)
        } catch (err) {
          console.error('删除图片失败:', err)
          this.$toast?.fail?.(err?.response?.data?.message || '删除失败')
          return
        }
      }
      this.images.splice(index, 1)
    },
    // ↑↓ 兜底排序（不依赖 hover/拖拽）：调整到第 1 位 = 成为主图（服务端同步 image=images[0]）
    moveImage (index, dir) {
      const to = index + dir
      if (to < 0 || to >= this.images.length) return
      const list = [...this.images]
      const [moved] = list.splice(index, 1)
      list.splice(to, 0, moved)
      this.applyReorder(list)
    },
    // 应用新顺序：先本地生效（跟手），编辑态再走专用 order API（同集合重排，防并发覆盖）；失败回滚
    async applyReorder (newList) {
      const prev = [...this.images]
      this.images = newList
      if (!(this.isEdit && this.productId)) return
      try {
        await reorderProductImages(this.productId, newList)
      } catch (err) {
        console.error('图片排序失败:', err)
        this.images = prev
        this.$toast?.fail?.(err?.response?.data?.message || '排序失败，已还原')
      }
    },
    // ===== 长按拖拽排序（触屏）：按下 450ms 不动激活，激活后 touchmove preventDefault 防滚屏 =====
    // 铁律（vue-touchstart-preventdefault-trap）：touchstart 绝不 preventDefault——否则吞合成
    // click，预览/删除/↑↓ 全部失灵；滚动交给 CSS touch-action:pan-y（纵向滚屏，横向拖拽）
    onCellTouchStart (e, index) {
      if (this.dragState.active) return
      const t = e.touches[0]
      const s = this.dragState
      s.from = index
      s.target = index
      s.startX = t.clientX
      s.startY = t.clientY
      s.x = t.clientX
      s.y = t.clientY
      s.timer = setTimeout(() => {
        s.active = true
        // 缓存各格位置（激活时布局静止；拖动期间网格不重排，无需重算）
        const grid = this.$refs.imgGrid
        const cells = (grid && grid.querySelectorAll('.img-cell')) || []
        s.rects = Array.from(cells).map(el => el.getBoundingClientRect())
        if (navigator.vibrate) { try { navigator.vibrate(15) } catch (_) {} }
      }, 450)
    },
    onCellTouchMove (e) {
      const s = this.dragState
      if (!s.active) {
        // 未激活期间出现滑动手势（页面滚动意图）：取消长按计时，交给浏览器
        const t = e.touches[0]
        if (Math.abs(t.clientX - s.startX) > 10 || Math.abs(t.clientY - s.startY) > 10) {
          clearTimeout(s.timer)
        }
        return
      }
      e.preventDefault()   // 激活后防页面滚动（元素级监听非 passive，可生效）
      const t = e.touches[0]
      s.x = t.clientX
      s.y = t.clientY
      let hit = -1
      for (let i = 0; i < s.rects.length; i++) {
        const r = s.rects[i]
        if (t.clientX >= r.left && t.clientX <= r.right && t.clientY >= r.top && t.clientY <= r.bottom) { hit = i; break }
      }
      s.target = hit
    },
    onCellTouchEnd () {
      const s = this.dragState
      clearTimeout(s.timer)
      if (!s.active) {
        s.from = -1
        s.target = -1
        return
      }
      const from = s.from
      const to = s.target
      s.active = false
      s.from = -1
      s.target = -1
      s.rects = []
      // 拖拽刚结束：短暂吞掉随后的合成 click（防误触预览）
      this.dragJustEnded = true
      setTimeout(() => { this.dragJustEnded = false }, 350)
      if (to < 0 || to === from) return
      const list = [...this.images]
      const [moved] = list.splice(from, 1)
      list.splice(to, 0, moved)
      this.applyReorder(list)
    },
    // 点缩略图全屏预览（预览态不提供删除/排序，回网格操作）
    previewImage (index) {
      if (this.dragJustEnded || this.dragState.active) return
      const url = this.images[index]
      if (!url) return
      this.previewIndex = index
      this.previewUrl = url
    },
    validateForm () {
      if (!this.form.name?.trim()) {
        this.$toast?.fail?.('请输入商品名称')
        return false
      }
      if (!this.form.price || this.form.price <= 0) {
        this.$toast?.fail?.('请输入正确的售价')
        return false
      }
      if (this.form.stock === null || this.form.stock < 0) {
        this.$toast?.fail?.('请输入库存数量')
        return false
      }
      return true
    },
    async saveProduct () {
      if (!this.validateForm()) return
      if (this.saving) return

      this.saving = true
      try {
        // 规范化单位列表（后端 saveUnits 双写旧 unit/pack_unit 字段，保证兼容）
        const units = this.form.units
          .filter(u => u.unit_name && String(u.unit_name).trim())
          .map(u => ({ name: String(u.unit_name).trim(), ratio: u.is_base ? 1 : (Number(u.ratio) || 1), is_base: u.is_base ? 1 : 0 }))
        const baseUnit = units.find(u => u.is_base) || units[0]
        // 准备提交数据（product_specs/applicable_models 由 ...this.form 带出；search_tags 从输入框同步防漏 blur）
        const data = {
          ...this.form,
          units,                                  // 包装单位换算体系（product_units 表）
          unit: baseUnit ? baseUnit.name : '个',  // 派生基础单位名（兼容旧字段；saveUnits 也会双写）
          search_tags: this.searchTagsInput.split(/[,，]/).map(s => s.trim()).filter(Boolean),
          image: this.images[0] || '',  // 后端会强制 image=images[0]，前端也传（新建路径）
          images: JSON.stringify(this.images),
          marketing_image: this.form.marketing_image || ''
        }
        // 图片管理收口（2026-09-19）：编辑态图片已随操作即时落库（专用 API），
        // 保存表单不再携带 image/images —— 整对象 PUT 覆盖会清掉并发期间他人刚保存的图
        if (this.isEdit) {
          delete data.image
          delete data.images
        }

        if (this.isEdit) {
          await this.axios.put(`/admin/products/${this.productId}`, data)
          // 边框三态独立提交（bindings/opt-outs 两表；失败不回滚商品保存，仅提示）
          if (this.form.frame_mode === 'frame' && !this.form.frame_id) {
            this.$toast?.fail?.('单独设置边框需先选择边框，商品已保存')
          } else {
            try {
              await this.axios.put(`/admin/products/${this.productId}/frame`, { mode: this.form.frame_mode, frame_id: this.form.frame_mode === 'frame' ? this.form.frame_id : null })
            } catch (fErr) {
              console.error('边框绑定失败:', fErr)
              this.$toast?.fail?.('边框绑定失败，商品已保存')
            }
          }
          this.$toast?.success?.('保存成功')
        } else {
          await this.axios.post('/admin/products', data)
          this.$toast?.success?.('创建成功')
        }
        this.$router.back()
      } catch (err) {
        console.error('保存失败:', err)
        this.$toast?.fail?.(this.isEdit ? '保存失败' : '创建失败')
      } finally {
        this.saving = false
      }
    },
    confirmDelete () {
      this.showDeleteConfirm = true
    },
    async doDelete () {
      try {
        await this.axios.delete(`/admin/products/${this.productId}`)
        this.$toast?.success?.('删除成功')
        this.$router.back()
      } catch (err) {
        console.error('删除失败:', err)
        this.$toast?.fail?.('删除失败')
      } finally {
        this.showDeleteConfirm = false
      }
    },
    // 新增搜索词
    async addSearchTerm () {
      const t = this.newSearchTerm
      if (!t.term || !t.term.trim()) { this.$toast?.fail?.('请输入搜索词'); return }
      try {
        const res = await this.axios.post('/admin/product-search-terms', {
          product_id: this.productId,
          term: t.term.trim(),
          term_type: t.term_type,
          weight: t.weight
        })
        if (res.data.code === 200) {
          this.searchTerms.push({ id: res.data.data.id, term: t.term.trim(), term_type: t.term_type, weight: t.weight, status: 'active' })
          this.newSearchTerm = { term: '', term_type: 'alias', weight: 5 }
          this.$toast?.success?.('已添加')
        } else {
          this.$toast?.fail?.(res.data.message || '添加失败')
        }
      } catch (err) {
        console.error('添加搜索词失败:', err)
        this.$toast?.fail?.('添加失败')
      }
    },
    // 保存搜索词修改（term/term_type/weight/status）
    async saveSearchTerm (st) {
      try {
        const res = await this.axios.put(`/admin/product-search-terms/${st.id}`, {
          term: st.term, term_type: st.term_type, weight: st.weight, status: st.status
        })
        if (res.data.code === 200) {
          this.$toast?.success?.('已保存')
        } else {
          this.$toast?.fail?.(res.data.message || '保存失败')
        }
      } catch (err) {
        console.error('保存搜索词失败:', err)
        this.$toast?.fail?.('保存失败')
      }
    },
    // 删除搜索词
    async removeSearchTerm (st, idx) {
      if (!confirm(`确定删除搜索词"${st.term}"？`)) return
      try {
        const res = await this.axios.delete(`/admin/product-search-terms/${st.id}`)
        if (res.data.code === 200) {
          this.searchTerms.splice(idx, 1)
          this.$toast?.success?.('已删除')
        } else {
          this.$toast?.fail?.(res.data.message || '删除失败')
        }
      } catch (err) {
        console.error('删除搜索词失败:', err)
        this.$toast?.fail?.('删除失败')
      }
    }
  }
}
